import { useQuery } from '@tanstack/react-query';
import { t } from 'i18next';
import {
  ChevronRight,
  ExternalLink,
  Play,
  Users,
  Workflow,
} from 'lucide-react';
import { Outlet, useNavigate, useParams } from 'react-router-dom';

import { McpSvg } from '@/assets/img/custom/mcp';
import SidebarLayout, { SidebarItem } from '@/app/components/sidebar-layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { CopyButton } from '@/components/ui/copy-button';
import { LoadingScreen } from '@/components/ui/loading-screen';
import { cn } from '@/lib/utils';
import { AdminUser } from '@activepieces/shared';
import { adminApi } from '@/features/internal-admin/lib/admin-api';

export type UserDetailContext = {
  userId: string;
  tenantId: string;
};

const UserDetailPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();

  const {
    data: user,
    isLoading,
    isError,
  } = useQuery<AdminUser, Error>({
    queryKey: ['admin-user', userId],
    queryFn: () => adminApi.getUser(userId!),
    enabled: !!userId,
  });

  if (isLoading) {
    return <LoadingScreen mode="container" />;
  }

  if (isError || !user) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-destructive">{t('User not found')}</p>
      </div>
    );
  }

  const basePath = `/internal-admin/users/${userId}`;

  const navItems: SidebarItem[] = [
    {
      title: t('Flows'),
      href: `${basePath}/flows`,
      icon: <Workflow size={20} />,
    },
    {
      title: t('Executions'),
      href: `${basePath}/executions`,
      icon: <Play size={20} />,
    },
    {
      title: t('MCPs'),
      href: `${basePath}/mcps`,
      icon: <McpSvg width={20} height={20} />,
    },
  ];

  const titleElement = (
    <div className="flex items-center gap-1">
      <Button
        variant="ghost"
        size="icon"
        onClick={() => navigate('/internal-admin/overview/users')}
      >
        <Users className="h-5 w-5" />
      </Button>
      <ChevronRight className="h-4 w-4 text-muted-foreground" />
      <h2 className="font-semibold tracking-tight">
        {user.firstName} {user.lastName}
      </h2>
    </div>
  );

  const userCard = (
    <Card>
      <CardContent className="flex flex-col gap-3 p-4">
        <div>
          <p className="text-xs text-muted-foreground">{t('Name')}</p>
          <p className="text-sm font-medium truncate">
            {user.firstName} {user.lastName}
          </p>
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{t('Email')}</p>
          <p className="text-sm font-medium truncate">{user.email}</p>
        </div>
        <div className="flex items-center justify-between flex-wrap gap-2">
          {user.externalId && (
            <div className="shrink-0">
              <p className="text-xs text-muted-foreground">
                {t('User ID')}
              </p>
              <div className="flex items-center gap-1">
                <p className="text-sm font-medium">{user.externalId}</p>
                <CopyButton
                  textToCopy={user.externalId}
                  variant="ghost"
                  size="icon"
                  className="h-4 w-4 shrink-0"
                />
              </div>
            </div>
          )}
          {user.globalCode && (
            <div className="shrink-0">
              <p className="text-xs text-muted-foreground">
                {t('Company code')}
              </p>
              <p className="text-sm font-medium">{user.globalCode}</p>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between flex-wrap gap-3 mt-3">
          {user.externalCustomerId && (
            <a
              href={`${
                import.meta.env.VITE_KIZEO_OAUTH_URL
              }/admin/customer_details/${user.externalCustomerId}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              <Button size="sm">
                <ExternalLink className="h-4 w-4 mr-2" />
                {t('Kizeo client sheet')}
              </Button>
            </a>
          )}
          <Badge
            variant="outline"
            className={cn(
              'w-fit text-sm px-3 py-1 ml-auto',
              user.status === 'ACTIVE'
                ? 'bg-green-100 text-green-700 border-green-200'
                : 'bg-red-100 text-red-700 border-red-200',
            )}
          >
            {user.status === 'ACTIVE' ? t('Active') : t('Inactive')}
          </Badge>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <SidebarLayout
      title={titleElement}
      items={navItems}
      sidebarHeader={userCard}
    >
      <Outlet
        context={{ userId: user.id, tenantId: user.tenantId }}
      />
    </SidebarLayout>
  );
};

export default UserDetailPage;
